import * as R from 'ramda';
import * as date from 'date-fns';

import getFilters from '../../selectors/getSelectedFilters';
import getUseRightsholderAttribution from '../../utils/getUseRightsholderAttribution';
import { isFailedToFetchError } from '../../utils/requestErrorHandlers';
import fetchMediaValueReportCached, {
    isMediaValueReportRequestError,
} from './utils/fetchMediaValueReportCached';
import hasFiltersChanged from './utils/hasFiltersChanged';

const getTopPlatforms =
    store =>
    async (state, granularity = 'week') => {
        const userClientId = state.user.selectedUserClientId;
        const clientId = state.user.selectedClientId;
        const filters = state.clients[userClientId].filters;
        const client = state.user.clientsById[clientId];
        const useRightsholderAttribution = getUseRightsholderAttribution(
            client,
            clientId,
        );

        const changesId = filters.changesId;
        const { total } = state.clients[userClientId].home.topPlatform;

        // The graph is built from the top-10 list, so it must wait for the
        // list requested under the current filters. While that request is in
        // flight its caller fetches the graph again once it lands; if it
        // failed, the graph cannot be built either.
        if (total.changesId !== changesId) {
            if (!total.isLoading && total.hasError) {
                store.setState(
                    R.assocPath(
                        [
                            'clients',
                            userClientId,
                            'home',
                            'topPlatform',
                            'graph',
                        ],
                        {
                            ...state.clients[userClientId].home.topPlatform
                                .graph,
                            isLoading: false,
                            hasError: true,
                        },
                        state,
                    ),
                    true,
                );
            }
            return;
        }

        // Sets Cards as loading
        const loadingState = R.pipe(
            R.assocPath(
                [
                    'clients',
                    userClientId,
                    'home',
                    'topPlatform',
                    'graph',
                    'isLoading',
                ],
                true,
            ),
            R.assocPath(
                [
                    'clients',
                    userClientId,
                    'home',
                    'topPlatform',
                    'graph',
                    'hasError',
                ],
                false,
            ),
        )(state);
        store.setState(loadingState);

        const globFilters = getFilters(filters);

        const topPlatformList =
            store.getState().clients[userClientId].home.topPlatform.total.list;
        const list = Array.isArray(topPlatformList) ? topPlatformList : [];

        // Get data for graphs based on the top 10 list
        const platformPostTypePairs = list.reduce((arr, { id }) => {
            arr.push({ platformId: id });
            return arr;
        }, []);

        const graphQuery = {
            ...globFilters,
            groupBy: ['socialPlatform', 'date'],
            orderBy: ['date-asc'],
            displayColumns: ['sponsorMediaValue'],
            platformPostTypePairs,
            includeTextTypeExposures: true,
            ...(useRightsholderAttribution
                ? { useRightsholderAttribution: true }
                : {}),
        };

        let graphData;

        try {
            graphData = await fetchMediaValueReportCached({
                ...graphQuery,
                granularity,
            });
        } catch (error) {
            if (
                !isFailedToFetchError(error) &&
                !isMediaValueReportRequestError(error)
            ) {
                throw error;
            }

            if (hasFiltersChanged(store, userClientId, changesId)) return;

            store.setState(
                R.pipe(
                    R.assocPath(
                        [
                            'clients',
                            userClientId,
                            'home',
                            'topPlatform',
                            'graph',
                            'isLoading',
                        ],
                        false,
                    ),
                    R.assocPath(
                        [
                            'clients',
                            userClientId,
                            'home',
                            'topPlatform',
                            'graph',
                            'hasError',
                        ],
                        true,
                    ),
                )(store.getState()),
                true,
            );
            return;
        }

        const graphValues = Array.isArray(graphData?.values)
            ? graphData.values
            : [];

        const startDate =
            globFilters.startDate || R.path([0, 'date'], graphValues);
        const endDate =
            globFilters.endDate ||
            (graphValues.length > 0
                ? R.path([graphValues.length - 1, 'date'], graphValues)
                : undefined);

        const parsedStartDate = startDate
            ? date.parseISO(startDate)
            : date.parseISO(
                  date.format(date.subMonths(new Date(), 3), 'yyyy-MM-dd'),
              );
        const parsedEndDate = endDate
            ? date.parseISO(endDate)
            : date.parseISO(date.format(new Date(), 'yyyy-MM-dd'));

        // generate Labels based on granularity
        const datesRange = date.eachDayOfInterval({
            start: parsedStartDate,
            end: parsedEndDate,
        });

        const labels = datesRange.reduce((acc, d) => {
            const currentDate = date.format(d, 'yyyy-MM-dd');
            if (granularity === 'day') acc.push(currentDate);
            if (
                granularity === 'week' &&
                date.isMonday(date.parseISO(currentDate))
            )
                acc.push(currentDate);
            if (
                granularity === 'month' &&
                date.isFirstDayOfMonth(date.parseISO(currentDate))
            )
                acc.push(currentDate);
            return acc;
        }, []);

        const dataMap = graphValues.reduce((acc, o) => {
            const itExists = !!acc[o.date];
            if (itExists) acc[o.date][o.socialPlatformId] = o.sponsorMediaValue;
            else acc[o.date] = { [o.socialPlatformId]: o.sponsorMediaValue };
            return acc;
        }, {});

        const data = list.reduce((acc, plt) => {
            const values = labels.map(date => {
                return R.path([date, plt.id], dataMap) || 0;
            });

            acc[plt.id] = {
                id: plt.id,
                label: plt.name,
                data: values,
            };

            return acc;
        }, {});

        if (hasFiltersChanged(store, userClientId, changesId)) return;

        store.setState(
            R.assocPath(
                ['clients', userClientId, 'home', 'topPlatform', 'graph'],
                {
                    isLoading: false,
                    hasError: false,
                    labels,
                    data,
                    granularity,
                    changesId,
                },
                store.getState(),
            ),
            true,
        );
    };

export default getTopPlatforms;
